import { action, computed, makeObservable, observable } from 'mobx';
import { DashboardCrumbs, DashboardDatePeriods, DashboardPlates, defaultDashboardFilters, defaultDAshboardPlatesData } from '../constants/dashboard';
import { dashboardPlatesDataCast, getFetchReportParams } from '../helpers/dashboard';
import { IDashboardFilters, IDashboardPlatePermission, TDashboardPlatesData } from '../types/dashboard';
import { USER_PERMISSIONS } from '~/constants/user';
import client from '~/services/client';
import { LoadingStatus } from '~/types/main';
import {
  branchesBodyCast,
  deliveryZonesBodyCast,
  orderingChannelsReportBodyCast,
  TBranchesReport,
  TDeliveryZonesReport,
  TOrderingChannelsReport,
  TResponseBranchesReport,
  TResponseDeliveryZonesReport,
  TResponseOrderingChannelsReport,
} from '~/types/reports';
import { hasPermissions } from '~/utils/helpers';
import { getAbortController, isCanceledRequest } from '~/helpers';
import logger from '~/services/logger';

class DashboardStore {
  private initial: boolean = true;

  constructor() {
    makeObservable(this);
  }

  private getPlatesDataController = getAbortController();
  private getReportController = getAbortController();

  @observable platesLoading: LoadingStatus = LoadingStatus.Init;
  @observable reportLoading: LoadingStatus = LoadingStatus.Init;

  @observable platesData: TDashboardPlatesData = defaultDAshboardPlatesData;

  @observable reportBranches: TBranchesReport = {};
  @observable reportDeliveryZones: TDeliveryZonesReport = {};
  @observable reportOrderingChannel: TOrderingChannelsReport = {};

  @observable selectedPlate: DashboardPlates | null = null;
  @observable selectedCrumb: DashboardCrumbs | null = null;

  @observable filters: IDashboardFilters = defaultDashboardFilters;

  @computed get isFirstLoading(): boolean {
    return this.initial && (this.isPlatesLoading || this.isReportLoading);
  }

  @computed get isPlatesLoading(): boolean {
    return [LoadingStatus.Loading, LoadingStatus.Refreshing].includes(this.platesLoading);
  }

  @computed get isReportLoading(): boolean {
    return !this.isPlatesLoading && [LoadingStatus.Loading, LoadingStatus.Refreshing].includes(this.reportLoading);
  }

  @computed get isMonthFilter(): boolean {
    return this.filters.datePeriod === DashboardDatePeriods.MONTH;
  }

  @computed get dashboardPermission(): Record<DashboardPlates, IDashboardPlatePermission> {
    return {
      [DashboardPlates.ORDERS]: {
        showPlate: hasPermissions(USER_PERMISSIONS.DASHBOARD),
        showLink: hasPermissions(USER_PERMISSIONS.REPORTS),
        showDetails: hasPermissions(USER_PERMISSIONS.REPORTS),
      },
      [DashboardPlates.REVENUE]: {
        showPlate: hasPermissions(USER_PERMISSIONS.DASHBOARD),
        showLink: hasPermissions(USER_PERMISSIONS.REPORTS),
        showDetails: hasPermissions(USER_PERMISSIONS.REPORTS),
      },
      [DashboardPlates.CRM]: {
        showPlate: hasPermissions(USER_PERMISSIONS.CUSTOMERS_VIEW),
        showLink: hasPermissions(USER_PERMISSIONS.CUSTOMERS_VIEW),
        showDetails: false,
      },
      [DashboardPlates.MARKETING]: {
        showPlate: hasPermissions(USER_PERMISSIONS.PROMOTION),
        showLink: hasPermissions(USER_PERMISSIONS.PROMOTION),
        showDetails: false,
      },
    };
  }

  public init = () => {
    this.selectedPlate = null;
    this.selectedCrumb = null;
    this.filters = defaultDashboardFilters;
    this.fetchPlatesData();
  };

  @action private fetchPlatesData = async () => {
    this.getPlatesDataController.abort();
    this.getPlatesDataController = getAbortController();

    try {
      this.platesLoading = LoadingStatus.Loading;

      const response = await client.get('v3/dashboard', {
        params: { period: this.filters.datePeriod },
        signal: this.getPlatesDataController.signal,
      });

      this.platesData = dashboardPlatesDataCast(response);
      this.platesLoading = LoadingStatus.Success;
      this.initial = false;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.platesLoading = LoadingStatus.Success;
        return;
      }

      this.platesLoading = LoadingStatus.Error;
      logger.error({ error, place: 'dashboardStore fetchPlatesData', showModal: true });
    }
  };

  @action fetchReport = async (crumb: DashboardCrumbs | null) => {
    if (!this.selectedPlate || !crumb) return;

    this.getReportController.abort();
    this.getReportController = getAbortController();

    try {
      this.reportLoading = LoadingStatus.Loading;

      const response: TResponseBranchesReport | TResponseDeliveryZonesReport | TResponseOrderingChannelsReport = await client.get(
        `v3/reports/sales/${crumb}`,
        {
          params: getFetchReportParams(this.filters.datePeriod),
          signal: this.getReportController.signal,
        }
      );

      switch (crumb) {
        case DashboardCrumbs.BRANCHES:
          Object.entries(response).map(([key, value]) => {
            this.reportBranches[key] = branchesBodyCast(value);
          });
          break;
        case DashboardCrumbs.ZONES:
          Object.entries(response).map(([key, value]) => {
            this.reportDeliveryZones[key] = deliveryZonesBodyCast(value);
          });
          break;
        case DashboardCrumbs.CHANNELS:
          Object.entries(response).map(([key, value]) => {
            this.reportOrderingChannel[key] = orderingChannelsReportBodyCast(value);
          });
          break;
        default:
          break;
      }

      this.reportLoading = LoadingStatus.Success;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.reportLoading = LoadingStatus.Success;
        return;
      }

      this.reportLoading = LoadingStatus.Error;
      logger.error({ error, place: 'dashboardStore fetchReport', showModal: true });
    }
  };

  @action selectPlate = (plate: DashboardPlates) => {
    if (this.selectedPlate === plate) {
      this.selectedPlate = null;
      this.selectCrumb(null);
      return;
    }

    this.selectedPlate = plate;
    // TODO: change to BRANCHES in Dashboard step 2
    this.selectCrumb(DashboardCrumbs.CHANNELS);
  };

  @action selectCrumb = (section: DashboardCrumbs | null) => {
    this.selectedCrumb = section;
    this.fetchReport(section);
  };

  @action updateFilters = (filters: Partial<IDashboardFilters>) => {
    this.filters = { ...this.filters, ...filters };

    this.fetchPlatesData();
    this.fetchReport(this.selectedCrumb);
  };
}

export const dashboardStore = new DashboardStore();
